import {Card, CardContent, CardHeader, Checkbox, Grid, Theme, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import clsx from 'clsx';
import React from 'react';
import {formatDate, formatMoney} from '../../../utils/formatters';
import {PaymentScheduleAction, PaymentScheduleAmountType} from '../../../graphql/payment_schedule.datatypes';
import getBillingParty from '../../BillingParty/getBillingParty.api';
import moment from 'moment';
import getPaymentSchedule from '../../PaymentSchedule/getPaymentSchedule.api';
import InfoModal from '../../BookingTool/InfoModal';


const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  content: {
    padding: 0
  },
  inner: {
    minWidth: 800
  },
  actions: {
    justifyContent: 'flex-end'
  },

  padBottom: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  heading: {
    fontFamily: 'Recoleta-SemiBold',
  },
  scheduleHeaderFooter: {
    backgroundColor: '#AD02A3',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
  },
  scheduleFooter: {
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: theme.palette.white,
    marginBottom: theme.spacing(2.5),
  },
  scheduleHeaderFooterText: {
    color: theme.palette.white,
    fontFamily: 'Nunito',
    fontSize: 16,
    fontWeight: 'bold',
  },
  scheduleBody: {
    backgroundColor: 'rgba(173, 2, 163, 0.1)',
    padding: theme.spacing(2),
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: theme.palette.white,
  },
  scheduleBodyText: {
    fontFamily: 'Nunito',
    fontSize: 16,
    fontWeight: 'bold',
  },
  marinBlock: {
    margin: theme.spacing(2),
  },
  body: {
    paddingBottom: theme.spacing(2),
    fontFamily: 'Nunito',
    fontSize: 18,
    letterSpacing: 30 / 1000,

    '& a': {
      color: theme.palette.secondary.main,
      fontWeight: 'bold',

      '& + a': {
        marginLeft: theme.spacing(3),
      },
    },

    '& p': {
      paddingBottom: theme.spacing(2),
    },
    '& ul': {
      color: theme.palette.black,
      listStyle: 'none',
      paddingBottom: theme.spacing(2),

      '& li': {
        paddingBottom: theme.spacing(2),
        '&::before': {
          content: '"o"',
          color: theme.palette.primary.main,
          fontWeight: 'bold',
          fontSize: 14,
          marginRight: theme.spacing(1),
        }
      },
    },
    '& h3': {
      paddingBottom: theme.spacing(2),
    },
  },
  termsLink: {
    color: theme.palette.secondary.main,
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  cancellationLink: {
    color: theme.palette.secondary.main,
    fontWeight: 'bold',
    cursor: 'pointer',
    marginLeft: theme.spacing(3),
  },
}));

const PaymentSchedule = (props: { className?: string, billingPartyId: number }) => {
  const {className, billingPartyId,...rest} = props;
  const classes = useStyles();
  const billingPartyData = getBillingParty(billingPartyId);
  const billingParty = billingPartyData && billingPartyData.data && billingPartyData.data.billingParty;
  const group = billingParty && billingParty.group;

  const paymentScheduleData = getPaymentSchedule(billingParty ? billingParty.payment_schedule!.id : -1);
  const paymentSchedule = paymentScheduleData && paymentScheduleData.data && paymentScheduleData.data.paymentSchedule;

  const paymentDates = paymentSchedule && paymentSchedule.dates && paymentSchedule.dates
      .filter(date => date.action_type === PaymentScheduleAction.PAYMENT)
      .sort((a, b) => moment(a.due_date!).unix() - moment(b.due_date!).unix());

  let runningTotal = 0;
  if(billingParty && paymentDates) {
    paymentDates.map(date => {
      switch (date.amount_type) {
        case PaymentScheduleAmountType.FIXED:
          date.calculatedAmount = date.amount;
          runningTotal += date.calculatedAmount!;
          break;
        case PaymentScheduleAmountType.PERCENT:
          date.calculatedAmount = Math.ceil(billingParty.cost! * (date.amount! / 100));
          runningTotal += date.calculatedAmount!;
          break;
        case PaymentScheduleAmountType.FIXED_PER_PERSON:
          date.calculatedAmount = date.amount! * (billingParty.members ? billingParty.members.length : 0);
          runningTotal += date.calculatedAmount!;
          break;
        case PaymentScheduleAmountType.PERCENT_PER_PERSON:
          date.calculatedAmount = Math.ceil(billingParty.cost! * (date.amount! / 100)) * (billingParty.members ? billingParty.members.length : 0);
          runningTotal += date.calculatedAmount!;
          break;
        case PaymentScheduleAmountType.REMAINING:
          date.calculatedAmount = billingParty.cost! - billingParty.totalReceived!;
          break;
      }
    });
  }

  const [openTermsModal, setTermsModal] = React.useState(false);
  const handleTermsModalOpen = () => setTermsModal(true);
  const handleTermsModalClose = () => setTermsModal(false);

  const [openCancellationModal, setCancellationModal] = React.useState(false);
  const handleCancellationModalOpen = () => setCancellationModal(true);
  const handleCancellationModalClose = () => setCancellationModal(false);

  return (
      <Card
          {...rest}
          className={clsx(classes.root, className)}
      >
        <CardHeader title="Payment Schedule" />
        <CardContent>

          {billingParty && (
              <Grid container spacing={1} className={classes.marinBlock}>
                <Grid item md={3} xs={6}>
                  <Typography variant={'h3'}>GRAND TOTAL: {formatMoney(billingParty.cost!, true)}</Typography>
                </Grid>
                <Grid item md={3} xs={6}>
                  <Typography variant={'h3'}>
                    TOTAL OUTSTANDING: {formatMoney(billingParty.outstandingBalance!, true)}
                  </Typography>
                </Grid>
                <Grid item md={3} xs={6}>
                  <Typography variant={'h3'}>
                    TOTAL PAID: {formatMoney(billingParty.totalReceived!, true)}
                  </Typography>
                </Grid>
                <Grid item md={3} xs={6}>
                  <Typography variant={'h3'}>
                    TOTAL PAST DUE: {formatMoney(billingParty.overduePayments!, true)}
                  </Typography>
                </Grid>
              </Grid>
          )}

          {billingParty && (
              <Grid container>
                {paymentDates && paymentDates.map((date, i) => {
                  const headerFooter = (i === paymentDates.length - 1);

                  return (
                      <Grid item xs={12} key={i}
                            className={headerFooter ? classes.scheduleHeaderFooter : classes.scheduleBody}>
                        <Typography variant="body1"
                                    className={headerFooter ? classes.scheduleHeaderFooterText : classes.scheduleBodyText}>
                          {date.title}:&nbsp;
                          {headerFooter && (
                              <React.Fragment>
                                {formatMoney(date.calculatedAmount!, true)}
                              </React.Fragment>
                          )}
                          {i === paymentDates.length - 1 && (
                              <React.Fragment>&nbsp; DUE {formatDate(date.due_date)}</React.Fragment>
                          )}
                        </Typography>
                        {!headerFooter && (
                            <Typography variant="body1" className={classes.scheduleBodyText}>
                              {date.amount_type === PaymentScheduleAmountType.FIXED && ('$' +date.calculatedAmount+ ' ')}
                              {date.amount_type === PaymentScheduleAmountType.FIXED_PER_PERSON && ('$'+date.amount+' PER PERSON ')}
                              {date.amount_type === PaymentScheduleAmountType.PERCENT && ('$'+date.calculatedAmount+' ')}
                              {date.amount_type === PaymentScheduleAmountType.PERCENT_PER_PERSON && ('$'+date.amount+' PER PERSON ')}
                              {date.amount_type === PaymentScheduleAmountType.REMAINING && ('$'+date.calculatedAmount+' ')}

                                {i !== 0 && <React.Fragment>, DUE <strong>{formatDate(date.due_date)}</strong></React.Fragment>}
                            </Typography>
                        )}
                      </Grid>
                  )
                })}
              </Grid>
          )}
          <br/><br/>

          {billingParty && paymentSchedule && (
              <Grid container>
                <Grid item xs={12}>
                  <Typography variant="body1" className={classes.body}
                              dangerouslySetInnerHTML={{__html: paymentSchedule.description}}/>
                  <Typography variant="body1" className={classes.body}>
                    <span className={classes.termsLink} onClick={handleTermsModalOpen}>Terms & Conditions</span>
                    <span className={classes.cancellationLink} onClick={handleCancellationModalOpen}>Cancellation Policy</span>
                  </Typography>
                </Grid>
              </Grid>
          )}

          {group && openTermsModal && (
              <InfoModal onClose={handleTermsModalClose} open={true} title="Terms & Conditions"
                         body={group.tour!.terms_and_conditions}/>
          )}
          {group && openCancellationModal && (
              <InfoModal onClose={handleCancellationModalClose} open={true} title="Cancellation Policy"
                         body={group.tour!.cancellation_policy}/>
          )}
        </CardContent>
      </Card>
  );
};

export default PaymentSchedule;
